<template>
  <section class="transactions-history">
    <h2 v-translate>
      Your History
    </h2>

    <Notification
      v-if="error"
      icon-name="danger"
      :title="$gettext('Failed retrieving your history')"
      :message="error"
    />

    <div v-if="!history">
      <TransactionsHistorySkeleton />
      <PaginationDots
        :current-page="paginationCache.currentPage"
        :pages-count="paginationCache.pagesCount"
      />
    </div>

    <p
      v-else-if="!history.data.length"
      v-translate
    >
      You haven't made any payout request yet.
    </p>

    <div
      v-else
      class="table-responsive-small"
    >
      <table>
        <thead>
          <tr>
            <th
              v-translate
              class="text-right"
            >
              Payout
            </th>
            <th
              v-translate
              class="payout-type text-center"
            >
              Payout Type
            </th>
            <th
              v-translate
              class="text-center"
            >
              Requested On
            </th>
            <th
              v-translate
              class="payout-status text-center"
            >
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="transaction in history.data"
            :key="transaction.id"
            class="transaction-row"
          >
            <td
              :data-header="$gettext('Payout')"
              class="text-right"
            >
              {{
                ( transaction.requestedAmount && transaction.requestedCurrency )
                  ? formatPrice(transaction.requestedAmount, transaction.requestedCurrency)
                  : formatPrice(transaction.amount, 'GBP')
              }}
            </td>
            <td
              :data-header="$gettext('Payout Type')"
              class="payout-type text-center"
            >
              {{ transaction.type }}
            </td>
            <td
              :data-header="$gettext('Requested On')"
              class="text-center"
            >
              {{ formatDate(transaction.createdAt) }}
            </td>
            <td
              :data-header="$gettext('Status')"
              class="payout-status text-center"
            >
              <strong :class="getStatusClass(transaction.status)">
                {{ $gettext(getStatusText(transaction.status)) }}
              </strong>
            </td>
            <td>
              <a
                class="more-details"
                href="#"
                :title="$gettext('View details')"
                @click.prevent="openTransactionDetails(transaction.id)"
              >
                &mldr;
              </a>
            </td>
          </tr>
        </tbody>
      </table>

      <PaginationDots
        v-if="history.meta.pagesCount > 1"
        :current-page="history.meta.currentPage"
        :pages-count="history.meta.pagesCount"
        @select-page="fetchHistory"
      />

      <TransactionDetailsDialog ref="transactionDetailsDialog" />
    </div>
  </section>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Action } from 'vuex-class'
import Loader from '@/components/CommonUI/Loader.vue'
import Notification from '@/components/CommonUI/Notification.vue'
import PaginationDots from '@/components/CommonUI/PaginationDots.vue'
import TransactionDetailsDialog from '@/components/TransactionDetailsDialog.vue'
import TransactionsHistorySkeleton from '@/components/TransactionsHistorySkeleton.vue'
import transactionStatuses from '@/mixins/transactionStatuses'
import { TransactionStatus } from '@/constants'
import { getErrorMessage } from '@/helpers/errorHandler'
import { formatAmount } from '@/helpers/formatAmount'
import { formatDate } from '@/helpers/formatDate'
import { formatPrice } from '@/helpers/formatPrice'

interface History {
  data: Transaction[];
  meta: Pagination;
}

@Options({
  components: {
    Loader,
    Notification,
    PaginationDots,
    TransactionDetailsDialog,
    TransactionsHistorySkeleton,
  },
  mixins: [
    transactionStatuses,
  ],
})

export default class TransactionsHistory extends Vue {
  history: History | null = null
  error: string | null = null
  paginationCache = {
    currentPage: 0,
    pagesCount: 0,
  }

  $refs!: {
    transactionDetailsDialog: InstanceType<typeof TransactionDetailsDialog>,
  }

  @Action getUserTransactions!: (page: number) => Promise<History>

  formatAmount = formatAmount
  formatPrice = formatPrice
  formatDate = formatDate
  getStatusClass!: (status: TransactionStatus) => string
  getStatusText!: (status: TransactionStatus) => string

  created() {
    this.fetchHistory(1)
  }

  async fetchHistory(pageNumber: number) {
    this.history = null

    try {
      this.history = await this.getUserTransactions(pageNumber)
      this.paginationCache = { currentPage: this.history.meta.currentPage, pagesCount: this.history.meta.pagesCount }
    } catch (error) {
      this.error = getErrorMessage(error)
    }
  }

  refreshHistory() {
    const pageNumber = this.history?.meta.currentPage || 1
    this.fetchHistory(pageNumber)
  }

  openTransactionDetails(transactionId: number) {
    this.$refs.transactionDetailsDialog.show(transactionId)
  }
}
</script>

<style lang="scss">
.transactions-history {
  th,
  td {
    &.payout-type {
      padding-left: 4rem;
    }
    &.payout-status {
      padding-right: 4rem;
    }
  }
  .transaction-row {
    height: 58px;
  }
}

.more-details {
  color: var(--medium-grey);
  font-size: 2rem;
  letter-spacing: .25rem;
  line-height: 1rem;
  text-decoration: none;
}
</style>
